import React, { useEffect, useMemo, useRef, useState } from "react";
import { Prompt } from "react-router-dom";
import { Button, CircularProgress, FormControlLabel, IconButton, LinearProgress, MenuItem, Switch, Tab, Tabs, TextField } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import AddOutlinedIcon from "@material-ui/icons/AddOutlined";
import useStyles from "./styles";
import CrmService, { apiError, setCrmSettingsCache } from "./crmService";
import { PageHead, useCrmSettings, useToast } from "./crmUi";

const TABS = [
  { key: "programs", label: "Programs", help: "Programs parents can enquire about. Shown in the enquiry form, Quick add and the Enquiries filters." },
  { key: "sources", label: "Sources", help: "Where enquiries come from. \"Sibling\", \"Referral\" and \"Others\" ask for extra details. Enquiries from the public form are tagged \"Online form\" automatically." },
  { key: "lostReasons", label: "Lost reasons", help: "Asked whenever an enquiry is marked Lost, and offered as a filter on Enquiries." },
  // { key: "staff", label: "Staff", help: "Admissions staff for \"Assigned to\". With one person, new enquiries are assigned to them. Add an email to send them a daily summary." },
  { key: "reminders", label: "Reminders", help: "Daily summary of follow-ups due, and when an enquiry counts as having no follow-up." },
];
const LIST_KEYS = ["programs", "sources", "lostReasons"];
const RENAME_HELP = "Renaming an item also updates the enquiries that use it.";

const TIME_ZONES = ["Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Europe/London", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "Australia/Sydney"];

let rowSeq = 0;
const rowId = () => `r${(rowSeq += 1)}`;

/** Editable rows keep a stable id (so typing doesn't lose focus) and the saved name (to spot renames). */
const toRows = (list) => (list || []).map((value) => ({ id: rowId(), value, orig: value }));

const formFrom = (settings, tz) => ({
  programs: toRows(settings.programs),
  sources: toRows(settings.sources),
  lostReasons: toRows(settings.lostReasons),
  staff: (settings.staff || []).map((s) => ({ id: rowId(), name: s.name, email: s.email || "", orig: s.name })),
  drafts: { programs: "", sources: "", lostReasons: "" },
  noFollowUpDays: settings.noFollowUpDays,
  timeZone: tz,
  reminderEmails: (settings.reminderEmails || []).join(", "),
  dailySummaryEnabled: settings.dailySummaryEnabled !== false,
});

/** Final list for a key: edited rows plus anything still typed in the "Add" box. */
const listOf = (form, key) => {
  const out = [];
  [...form[key].map((r) => r.value), form.drafts[key]].forEach((v) => {
    const t = String(v || "").trim();
    if (t && !out.some((x) => x.toLowerCase() === t.toLowerCase())) out.push(t);
  });
  return out;
};

/** Rows whose saved name changed (and the old name is no longer in the list). */
const renamesOf = (rows, finalList, field = "value") =>
  rows
    .filter((r) => r.orig && String(r[field]).trim() && String(r[field]).trim() !== r.orig && !finalList.includes(r.orig))
    .map((r) => ({ from: r.orig, to: String(r[field]).trim() }));

const payloadOf = (form) => {
  const lists = Object.fromEntries(LIST_KEYS.map((k) => [k, listOf(form, k)]));
  const staff = form.staff.filter((s) => s.name.trim()).map((s) => ({ name: s.name.trim(), email: s.email.trim() }));
  return {
    ...lists,
    staff,
    noFollowUpDays: form.noFollowUpDays,
    timeZone: form.timeZone,
    reminderEmails: form.reminderEmails.split(/[,;\s]+/).filter(Boolean),
    dailySummaryEnabled: form.dailySummaryEnabled,
  };
};

function ListEditor({ rows, draft, onRows, onDraft, placeholder }) {
  const classes = useStyles();
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (!rows.some((r) => r.value.trim().toLowerCase() === v.toLowerCase())) {
      onRows([...rows, { id: rowId(), value: v, orig: "" }]);
    }
    onDraft("");
  };
  return (
    <div style={{ display: "grid", gap: 8, maxWidth: 520 }}>
      {rows.map((row, i) => (
        <div key={row.id} style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <TextField
            value={row.value}
            onChange={(e) => onRows(rows.map((r) => (r.id === row.id ? { ...r, value: e.target.value } : r)))}
            variant="outlined"
            size="small"
            fullWidth
            helperText={row.orig && row.value.trim() && row.value.trim() !== row.orig ? `Was "${row.orig}"` : ""}
            inputProps={{ "aria-label": `Item ${i + 1}`, maxLength: 80 }}
          />
          <IconButton className={classes.iconBtn} aria-label={`Remove ${row.value}`} onClick={() => onRows(rows.filter((r) => r.id !== row.id))}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      ))}
      <div style={{ display: "flex", gap: 8 }}>
        <TextField
          value={draft}
          onChange={(e) => onDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder={placeholder}
          variant="outlined"
          size="small"
          fullWidth
          inputProps={{ maxLength: 80, "aria-label": placeholder }}
        />
        <Button variant="outlined" startIcon={<AddOutlinedIcon />} onClick={add} disabled={!draft.trim()}>
          Add
        </Button>
      </div>
    </div>
  );
}

/** CRM settings: editable lists and reminders. Saved changes reach every CRM page. */
export default function CrmSettings() {
  const classes = useStyles();
  const { settings, loading, tz } = useCrmSettings();
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, toastNode] = useToast();
  const savedSnapshot = useRef("");

  useEffect(() => {
    if (settings && !form) {
      const f = formFrom(settings, tz);
      savedSnapshot.current = JSON.stringify(payloadOf(f));
      setForm(f);
    }
  }, [settings, form, tz]);

  const dirty = useMemo(() => Boolean(form) && JSON.stringify(payloadOf(form)) !== savedSnapshot.current, [form]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setDraft = (key, value) => setForm((f) => ({ ...f, drafts: { ...f.drafts, [key]: value } }));

  const save = async () => {
    setSaving(true);
    setErrors({});
    try {
      const body = payloadOf(form);
      body.renames = {
        ...Object.fromEntries(LIST_KEYS.map((k) => [k, renamesOf(form[k], body[k])])),
        staff: renamesOf(form.staff, body.staff.map((s) => s.name), "name"),
      };
      const res = await CrmService.saveSettings(body);
      const fresh = setCrmSettingsCache(await CrmService.getSettings());
      const f = formFrom(fresh.settings, fresh.timeZone);
      savedSnapshot.current = JSON.stringify(payloadOf(f));
      setForm(f);
      toast(res.message || "CRM settings saved.");
    } catch (err) {
      const e = apiError(err, "Could not save settings.");
      setErrors(e.errors);
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const current = TABS[tab];

  return (
    <div className={classes.page}>
      <Prompt when={dirty && !saving} message="You have unsaved CRM settings. Leave without saving?" />
      <PageHead
        title="CRM settings"
        subtitle="Lists and reminders for admissions enquiries. Saved changes apply to every CRM page."
      />
      {(loading || !form) && <LinearProgress />}
      {form && (
        <div className={classes.panel}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" indicatorColor="primary" textColor="primary">
            {TABS.map((t) => (
              <Tab key={t.key} label={t.label} style={{ textTransform: "none", minWidth: 0, marginRight: 20 }} />
            ))}
          </Tabs>
          <div style={{ borderTop: "1px solid #c4c4c4", paddingTop: 20 }}>
            <p className={classes.muted} style={{ marginTop: 0, maxWidth: 640, fontSize: 14 }}>
              {current.help}
              {LIST_KEYS.includes(current.key) && ` ${RENAME_HELP}`}
            </p>
            {errors[current.key] && <div className={classes.error} style={{ marginBottom: 12 }}>{errors[current.key]}</div>}

            {LIST_KEYS.includes(current.key) && (
              <ListEditor
                rows={form[current.key]}
                draft={form.drafts[current.key]}
                onRows={(rows) => set({ [current.key]: rows })}
                onDraft={(v) => setDraft(current.key, v)}
                placeholder={`Add ${current.label.toLowerCase().replace(/s$/, "")}`}
              />
            )}

            {current.key === "staff" && (
              <div style={{ display: "grid", gap: 8, maxWidth: 640 }}>
                {form.staff.map((s) => (
                  <div key={s.id} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <TextField
                      label="Name"
                      value={s.name}
                      onChange={(e) => set({ staff: form.staff.map((x) => (x.id === s.id ? { ...x, name: e.target.value } : x)) })}
                      variant="outlined"
                      size="small"
                      style={{ flex: "1 1 180px" }}
                    />
                    <TextField
                      label="Email (for daily summary)"
                      value={s.email}
                      onChange={(e) => set({ staff: form.staff.map((x) => (x.id === s.id ? { ...x, email: e.target.value } : x)) })}
                      variant="outlined"
                      size="small"
                      type="email"
                      style={{ flex: "1 1 220px" }}
                    />
                    <IconButton className={classes.iconBtn} aria-label={`Remove ${s.name || "staff"}`} onClick={() => set({ staff: form.staff.filter((x) => x.id !== s.id) })}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </div>
                ))}
                <div>
                  <Button variant="outlined" startIcon={<AddOutlinedIcon />} onClick={() => set({ staff: [...form.staff, { id: rowId(), name: "", email: "", orig: "" }] })}>
                    Add staff member
                  </Button>
                </div>
              </div>
            )}

            {current.key === "reminders" && (
              <div style={{ display: "grid", gap: 18, maxWidth: 520 }}>
                <FormControlLabel
                  control={<Switch checked={form.dailySummaryEnabled} onChange={(e) => set({ dailySummaryEnabled: e.target.checked })} color="primary" />}
                  label="Send a daily summary (email + notification) of follow-ups due"
                />
                <TextField
                  label="No follow-up after (days)"
                  type="number"
                  value={form.noFollowUpDays}
                  onChange={(e) => set({ noFollowUpDays: e.target.value })}
                  inputProps={{ min: 1, max: 90 }}
                  variant="outlined"
                  size="small"
                  error={Boolean(errors.noFollowUpDays)}
                  helperText={errors.noFollowUpDays || "Open enquiries with no follow-up for this many days are highlighted."}
                />
                <TextField
                  select
                  label="Time zone"
                  value={form.timeZone}
                  onChange={(e) => set({ timeZone: e.target.value })}
                  variant="outlined"
                  size="small"
                  helperText="Decides what counts as today and when the daily summary goes out."
                >
                  {[...new Set([form.timeZone, ...TIME_ZONES])].map((z) => (
                    <MenuItem key={z} value={z}>
                      {z}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Also send the full summary to"
                  value={form.reminderEmails}
                  onChange={(e) => set({ reminderEmails: e.target.value })}
                  placeholder="principal@school.com, office@school.com"
                  variant="outlined"
                  size="small"
                  error={Boolean(errors.reminderEmails)}
                  helperText={errors.reminderEmails || "The school email always gets it. Separate addresses with commas."}
                />
              </div>
            )}
          </div>
          {/* Panel footer: Save sits below the list it saves. */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              marginTop: 24,
              paddingTop: 16,
              borderTop: "1px solid #d9d9d9",
            }}
          >
            {dirty && <span className={classes.muted}>Unsaved changes</span>}
            <Button
              variant="contained"
              color="primary"
              onClick={save}
              disabled={saving || !dirty}
              startIcon={saving ? <CircularProgress size={16} style={{ color: "#fff" }} /> : null}
            >
              Save settings
            </Button>
          </div>
        </div>
      )}
      {toastNode}
    </div>
  );
}
